import { JSX, Suspense, useCallback, useEffect } from 'react'

import { useDisclosure } from '@chakra-ui/react'
import { DownloadIcon, FontSizeIcon, SearchIcon } from '@verbit-ai/icons-library'

import { FontSizeControl } from 'src/components/FontSizeControl/FontSizeControl'
import { Search } from 'src/components/Search/Search'
import { ThemeToggle } from 'src/components/ThemeToggle/ThemeToggle'
import { LanguageSelect } from 'src/components/LanguageSelect/LanguageSelect'
import { DisplaySettingsIcon, GlobeIcon } from 'src/components/icons'
import { SessionDetails } from 'src/components/SessionDetails/SessionDetails'
import { getLanguagePresentation } from 'src/utils/language'
import { analyticsService } from 'src/services/AnalyticsService'
import { useVerbitConnectAvailability } from 'src/hooks/useVerbitConnectAvailability'
import { useTranscriptDimensions } from 'src/hooks/useTranscriptDimensions'
import { useIsTranscriptEmpty } from 'src/contexts/Transcript/TranscriptContext'
import { MediaPlayer, MediaPlayerToggle, MediaRecorder, MediaSource } from 'src/components/VerbitWrappers/VerbitConnect'
import { useDownloadOptions } from 'src/hooks/useDownloadOptions'
import { DownloadControl } from 'src/components/DownloadControl'
import { useLanguage } from 'src/contexts/LanguageContext'

import { TranscriptToolbarButton } from './TranscriptToolbarButton'
import * as S from './styles'
import { ToolbarLayout, getToolbarLayout } from './types'

type TranscriptToolbarProps = {
    containerWidth: number
}

export const TranscriptToolbar = (props: TranscriptToolbarProps) => {
    const { containerWidth } = props
    const { translations, requestedLanguageCode, isLanguageSelectionAvailable } = useLanguage()
    const { isOpen: isFontSizeControlOpen, onOpen: onFontSizeControlOpen, onClose: onFontSizeControlClose } = useDisclosure()
    const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure()
    const { isOpen: isThemeToggleOpen, onOpen: onThemeToggleOpen, onClose: onThemeToggleClose } = useDisclosure()
    const { isOpen: isLanguageSelectOpen, onOpen: onLanguageSelectOpen, onClose: onLanguageSelectClose } = useDisclosure()
    const { isOpen: isDownloadControlOpen, onOpen: onDownloadControlOpen, onClose: onDownloadControlClose } = useDisclosure()
    const downloadOptions = useDownloadOptions()
    const isTranscriptEmpty = useIsTranscriptEmpty()
    const shouldShowSearchButton = !isTranscriptEmpty
    const { transcriptParagraphWidth } = useTranscriptDimensions(containerWidth)

    useEffect(() => {
        if (isLanguageSelectOpen) {
            analyticsService.track('TranslationToggled', {})
        }
    }, [isLanguageSelectOpen])

    const onBrowserSearch = useCallback(
        (event: KeyboardEvent) => {
            // we do not listen to every other combination that allows opening the search
            // (like F3 or Ctrl+G), because there's too much of them and this was not requested
            const isCtrlF = (event.ctrlKey || event.metaKey) && event.key === 'f'
            if (!isCtrlF || !shouldShowSearchButton) {
                return
            }
            event.preventDefault()
            onSearchOpen()
        },
        [onSearchOpen, shouldShowSearchButton],
    )
    useEffect(() => {
        addEventListener('keydown', onBrowserSearch)

        return () => {
            removeEventListener('keydown', onBrowserSearch)
        }
    }, [onBrowserSearch])

    const { isMediaRecorderAvailable, isMediaPlayerAvailable, isMediaSourceAvailable } = useVerbitConnectAvailability()

    const layoutType = getToolbarLayout({ isMediaRecorderAvailable, isMediaPlayerAvailable, isMediaSourceAvailable })
    const layoutsMap: Record<ToolbarLayout, JSX.Element> = {
        mediaRecorder: (
            <Suspense>
                <MediaRecorder />
            </Suspense>
        ),
        mediaPlayer: (
            <Suspense>
                <MediaPlayer canExitPlayback={false} />
            </Suspense>
        ),
        mediaSource: (
            <Suspense>
                <MediaSource />
            </Suspense>
        ),
        mediaRecorderAndPlayer: (
            <Suspense>
                <MediaRecorder />
                <S.MediaPlayerToggleWrapper>
                    <MediaPlayerToggle />
                </S.MediaPlayerToggleWrapper>
            </Suspense>
        ),
        mediaSourceAndPlayer: (
            <Suspense>
                <MediaSource />
                <S.MediaPlayerToggleWrapper>
                    <MediaPlayerToggle />
                </S.MediaPlayerToggleWrapper>
            </Suspense>
        ),
        noMedia: <SessionDetails maxWidth={transcriptParagraphWidth} />,
    }

    return (
        <S.TranscriptToolbarBar>
            <S.TranscriptToolbarLimiter $paragraphTotalWidth={transcriptParagraphWidth}>
                {layoutsMap[layoutType]}

                {shouldShowSearchButton && (
                    <Search isOpen={isSearchOpen} onOpen={onSearchOpen} onClose={onSearchClose}>
                        <TranscriptToolbarButton aria-label="Search in transcript" title="Search">
                            <SearchIcon color="currentColor" />
                        </TranscriptToolbarButton>
                    </Search>
                )}

                <FontSizeControl isOpen={isFontSizeControlOpen} onOpen={onFontSizeControlOpen} onClose={onFontSizeControlClose}>
                    <TranscriptToolbarButton aria-label="Font size" title="Font size">
                        <FontSizeIcon color="currentColor" />
                    </TranscriptToolbarButton>
                </FontSizeControl>

                {downloadOptions.length === 1 && (
                    <TranscriptToolbarButton
                        aria-label={downloadOptions[0].labelStandalone}
                        title={downloadOptions[0].labelStandalone}
                        onClick={downloadOptions[0].action}>
                        <DownloadIcon color="currentColor" />
                    </TranscriptToolbarButton>
                )}
                {downloadOptions.length > 1 && (
                    <DownloadControl
                        isOpen={isDownloadControlOpen}
                        onOpen={onDownloadControlOpen}
                        onClose={onDownloadControlClose}
                        options={downloadOptions}>
                        <TranscriptToolbarButton aria-label="Download" title="Download">
                            <DownloadIcon color="currentColor" />
                        </TranscriptToolbarButton>
                    </DownloadControl>
                )}

                {isLanguageSelectionAvailable && (
                    <LanguageSelect isOpen={isLanguageSelectOpen} onOpen={onLanguageSelectOpen} onClose={onLanguageSelectClose}>
                        <TranscriptToolbarButton aria-label="Select language" title="Select language">
                            <S.LanguageButtonContent>
                                <GlobeIcon color="currentColor" />
                                <S.LanguageName>{getLanguagePresentation(requestedLanguageCode, translations)}</S.LanguageName>
                            </S.LanguageButtonContent>
                        </TranscriptToolbarButton>
                    </LanguageSelect>
                )}

                <ThemeToggle isOpen={isThemeToggleOpen} onOpen={onThemeToggleOpen} onClose={onThemeToggleClose}>
                    <TranscriptToolbarButton
                        className={S.TOGGLE_THEME_BUTTON_CLASSNAME}
                        aria-label="Display settings"
                        title="Choose theme"
                        data-appcues-display-settings-button="display-settings-button">
                        <DisplaySettingsIcon color="currentColor" />
                    </TranscriptToolbarButton>
                </ThemeToggle>
            </S.TranscriptToolbarLimiter>
        </S.TranscriptToolbarBar>
    )
}
